import { useState, useEffect } from "react";
import { useBrandName } from "../lib/company";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import { useAuth } from "../lib/auth";
import { useDecryptCard, useCardActions, useCards } from "../hooks/use-cards";
import { GET_USER_CARDS, CHANGE_CARD_PIN } from "../graphql/cards";
import { GET_USER_ADDRESS, UPDATE_USER_ADDRESS } from "../graphql/user";
import { GET_TRANSACTIONS } from "../graphql/transactions";
import { TransactionList } from "../components/TransactionList";
import { MintSequence } from "../components/MintSequence";
import { SectionCard } from "../components/SectionCard";
import { GradientHeader } from "../components/GradientHeader";
import { PageContainer } from "../components/PageContainer";
import { CardSkeleton } from "../components/SkeletonLoader";
import { hapticFeedback, enableBackButton, showConfirm } from "../lib/telegram";
import { parseMoney } from "../lib/money";

export const CardDetail = () => {
  const brandName = useBrandName();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const cardType = searchParams.get("type") || "physical";
  const navigate = useNavigate();
  const { user } = useAuth();
  const { decryptCard, loading: decrypting } = useDecryptCard();
  const { activateCard, suspendCard, updateStatus, loading: actionLoading } = useCardActions();
  const { refetch: refetchCards } = useCards();

  const [revealedNumber, setRevealedNumber] = useState<string | null>(null);
  const [revealedCvv, setRevealedCvv] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showPinForm, setShowPinForm] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pinError, setPinError] = useState<string | null>(null);
  const [pinSuccess, setPinSuccess] = useState(false);
  const [changePin, { loading: pinLoading }] = useMutation(CHANGE_CARD_PIN);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [addressFields, setAddressFields] = useState({
    address_line1: "", address_line2: "", city: "", state: "", zip_code: "", country: "US",
  });
  const [addressError, setAddressError] = useState<string | null>(null);
  const [addressSuccess, setAddressSuccess] = useState(false);
  const [updateAddress, { loading: addressLoading }] = useMutation(UPDATE_USER_ADDRESS);
  const [showMint, setShowMint] = useState(false);

  useEffect(() => {
    return enableBackButton(() => navigate("/"));
  }, [navigate]);

  const { data: addressData } = useQuery(GET_USER_ADDRESS, {
    variables: { userId: user?.id },
    skip: !user?.id,
  });

  useEffect(() => {
    if (addressData?.users_by_pk) {
      const u = addressData.users_by_pk;
      setAddressFields({
        address_line1: u.address_line1 || "",
        address_line2: u.address_line2 || "",
        city: u.city || "",
        state: u.state || "",
        zip_code: u.zip_code || "",
        country: u.country || "US",
      });
    }
  }, [addressData]);

  const { data, loading } = useQuery(GET_USER_CARDS, {
    variables: { userId: user?.id },
    skip: !user?.id,
  });

  const { data: txData } = useQuery(GET_TRANSACTIONS, {
    variables: { userId: user?.id, limit: 10 },
    skip: !user?.id,
  });

  if (loading)
    return (
      <PageContainer noNav>
        <div className="p-4">
          <CardSkeleton />
        </div>
      </PageContainer>
    );

  const allCards = [
    ...(data?.physical_cards || []).map((c: any) => ({ ...c, _type: "physical" })),
    ...(data?.virtual_cards || []).map((c: any) => ({ ...c, _type: "virtual" })),
  ];
  const card = allCards.find((c: any) => c.id === id);

  if (!card) {
    return (
      <PageContainer noNav>
        <div className="p-4 text-center text-muted">Card not found</div>
      </PageContainer>
    );
  }

  const handleReveal = async () => {
    const confirmed = await showConfirm("Reveal full card number?");
    if (!confirmed) return;

    hapticFeedback.medium();
    const result = await decryptCard(
      card.id,
      cardType === "physical" ? "PHYSICAL" : "VIRTUAL"
    );

    if (result?.success) {
      setRevealedNumber(result.cardNumber);
      setRevealedCvv(result.vvc || null);
      hapticFeedback.success();
    } else {
      hapticFeedback.error();
    }
  };

  const handleCopy = async () => {
    if (revealedNumber) {
      await navigator.clipboard.writeText(revealedNumber);
      setCopied(true);
      hapticFeedback.light();
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleSetPin = async () => {
    if (!/^\d{4}$/.test(pinValue)) {
      setPinError("PIN must be exactly 4 digits");
      hapticFeedback.error();
      return;
    }
    setPinError(null);
    const confirmed = await showConfirm("Set this as your card PIN?");
    if (!confirmed) return;

    try {
      hapticFeedback.medium();
      const { data } = await changePin({
        variables: { input: { cardId: card.id, newPin: pinValue } },
      });
      if (data?.changeCardPin?.success) {
        hapticFeedback.success();
        setPinSuccess(true);
        setPinValue("");
        setTimeout(() => {
          setPinSuccess(false);
          setShowPinForm(false);
        }, 2000);
      } else {
        hapticFeedback.error();
        setPinError(data?.changeCardPin?.error || "Failed to set PIN");
      }
    } catch (err: any) {
      hapticFeedback.error();
      setPinError(err.message || "Failed to set PIN");
    }
  };

  const handleSaveAddress = async () => {
    if (!addressFields.address_line1.trim() || !addressFields.city.trim() || !addressFields.state.trim() || !addressFields.zip_code.trim()) {
      setAddressError("Please fill in all required fields");
      hapticFeedback.error();
      return;
    }
    setAddressError(null);
    try {
      hapticFeedback.medium();
      await updateAddress({
        variables: { userId: user?.id, ...addressFields },
      });
      hapticFeedback.success();
      setAddressSuccess(true);
      setTimeout(() => {
        setAddressSuccess(false);
        setShowAddressForm(false);
      }, 2000);
    } catch (err: any) {
      hapticFeedback.error();
      setAddressError(err.message || "Failed to save address");
    }
  };

  const balance =
    card._type === "physical"
      ? parseMoney(card.available_balance)
      : parseMoney(card.current_balance);

  const isPhysical = card._type === "physical";

  const statusStamp: Record<string, string> = {
    active: "text-mint",
    suspended: "text-warning",
    cancelled: "text-error",
  };
  const cardStatus = card.status?.toLowerCase() || "";

  if (showMint) {
    return (
      <MintSequence
        brandName={brandName}
        lastFour={card.card_number_last_four}
        balance={balance}
        stampText="Active"
        caption="available to spend"
        onComplete={() => setShowMint(false)}
      />
    );
  }

  return (
    <PageContainer noNav>
      <GradientHeader size="sm">
        <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted mb-1">
          {isPhysical ? "Physical card" : "Virtual card"}
        </p>
        <p className="font-display text-lg font-semibold text-ink tracking-[-0.02em]">
          {card.card_name || "Card"}
        </p>
      </GradientHeader>

      {/* The card object */}
      <div className="px-5 mt-5">
        <div className="card-shimmer bg-gradient-to-br from-[#12312C] via-card-obj to-[#071512] rounded-[14px] p-6 text-[#E8F5F2] shadow-[0_18px_40px_rgba(11,31,28,0.35)]">
          <div className="flex justify-between items-start mb-6">
            <span className={`stamp ${statusStamp[cardStatus] || "text-[#9DBDB6]"}`}>
              {cardStatus}
            </span>
            <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-[#9DBDB6]/70">
              {brandName.toUpperCase()}
            </p>
          </div>

          {/* Card number */}
          <div className="mb-6">
            {revealedNumber ? (
              <button onClick={handleCopy} className="text-left w-full">
                <p className="text-xl font-mono tracking-[0.14em]">
                  {revealedNumber.replace(/(.{4})/g, "$1 ").trim()}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#9DBDB6] mt-1">
                  {copied ? "Copied" : "Tap to copy"}
                </p>
              </button>
            ) : (
              <button
                onClick={handleReveal}
                disabled={decrypting}
                className="text-left w-full"
              >
                <p className="text-xl font-mono tracking-[0.14em] text-[#9DBDB6]">
                  •••• •••• •••• {card.card_number_last_four || "••••"}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[#9DBDB6] mt-1">
                  {decrypting ? "Revealing…" : "Tap to reveal"}
                </p>
              </button>
            )}
          </div>

          <div className="flex justify-between items-end">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#9DBDB6]">
                Balance
              </p>
              <p className="money text-2xl font-semibold">${balance.toFixed(2)}</p>
            </div>
            {(card.expiry_date || (card.expiry_month && card.expiry_year)) && (
              <div className="text-center">
                <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#9DBDB6]">
                  Exp
                </p>
                <p className="text-sm font-mono">
                  {card.expiry_date
                    ? `${card.expiry_date.slice(5, 7)}/${card.expiry_date.slice(2, 4)}`
                    : `${String(card.expiry_month).padStart(2, "0")}/${String(card.expiry_year).slice(-2)}`}
                </p>
              </div>
            )}
            {revealedCvv && (
              <div className="text-right">
                <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#9DBDB6]">
                  CVV
                </p>
                <p className="text-lg font-mono">{revealedCvv}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Card actions */}
      {cardStatus !== "cancelled" && cardStatus !== "closed" && (
        <div className="px-5 mt-4">
          {cardStatus === "active" && (
            <button
              disabled={actionLoading}
              onClick={async () => {
                const confirmed = await showConfirm("Suspend this card?");
                if (!confirmed) return;
                hapticFeedback.medium();
                try {
                  if (card._type === "virtual") {
                    const result = await suspendCard(card.id);
                    if (result?.success) {
                      hapticFeedback.success();
                      refetchCards();
                    } else {
                      hapticFeedback.error();
                    }
                  } else {
                    const result = await updateStatus(card.id, "SUSPENDED");
                    if (result?.success) {
                      hapticFeedback.success();
                      refetchCards();
                    } else {
                      hapticFeedback.error();
                    }
                  }
                } catch {
                  hapticFeedback.error();
                }
              }}
              className="w-full py-3 rounded-xl text-sm font-semibold bg-surface border border-warning/40 text-warning disabled:opacity-50"
            >
              {actionLoading ? "Processing…" : "Suspend card"}
            </button>
          )}
          {cardStatus === "suspended" && (
            <button
              disabled={actionLoading}
              onClick={async () => {
                const confirmed = await showConfirm("Activate this card?");
                if (!confirmed) return;
                hapticFeedback.medium();
                try {
                  if (card._type === "virtual") {
                    const result = await activateCard(card.id);
                    if (result?.success) {
                      setShowMint(true);
                      refetchCards();
                    } else {
                      hapticFeedback.error();
                    }
                  } else {
                    const result = await updateStatus(card.id, "ACTIVE");
                    if (result?.success) {
                      setShowMint(true);
                      refetchCards();
                    } else {
                      hapticFeedback.error();
                    }
                  }
                } catch {
                  hapticFeedback.error();
                }
              }}
              className="w-full py-3 rounded-xl text-sm font-semibold bg-ink text-ground disabled:opacity-50"
            >
              {actionLoading ? "Processing…" : "Activate card"}
            </button>
          )}
        </div>
      )}

      {/* Set PIN - physical cards only */}
      {isPhysical && cardStatus === "active" && (
        <div className="px-4 mt-4">
          <SectionCard title="Card PIN">
            {!showPinForm ? (
              <button
                onClick={() => { setShowPinForm(true); setPinError(null); setPinSuccess(false); }}
                className="w-full py-3 rounded-xl text-sm font-semibold bg-ground border border-hairline text-ink"
              >
                Set / Change PIN
              </button>
            ) : (
              <div className="space-y-3 pt-1">
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  value={pinValue}
                  onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ""))}
                  placeholder="Enter 4-digit PIN"
                  className="money w-full py-3 px-4 bg-surface text-ink rounded-xl border border-hairline outline-none focus:border-accent text-center text-2xl tracking-widest"
                />
                {pinError && (
                  <p className="text-xs text-error text-center">{pinError}</p>
                )}
                {pinSuccess && (
                  <p className="text-xs text-success text-center">PIN set successfully</p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowPinForm(false); setPinValue(""); setPinError(null); }}
                    className="flex-1 py-3 rounded-xl text-sm font-semibold bg-ground border border-hairline text-muted"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSetPin}
                    disabled={pinLoading || pinValue.length !== 4}
                    className="flex-1 py-3 rounded-xl text-sm font-semibold bg-ink text-ground disabled:opacity-50"
                  >
                    {pinLoading ? "Setting…" : "Confirm PIN"}
                  </button>
                </div>
              </div>
            )}
          </SectionCard>
        </div>
      )}

      {/* Billing Address - physical cards only */}
      {isPhysical && (
        <div className="px-4 mt-4">
          <SectionCard title="Billing address">
            {!showAddressForm ? (
              <div>
                {addressFields.address_line1 ? (
                  <div className="mb-3">
                    <p className="text-sm text-ink">{addressFields.address_line1}</p>
                    {addressFields.address_line2 && (
                      <p className="text-sm text-ink">{addressFields.address_line2}</p>
                    )}
                    <p className="text-sm text-ink">
                      {addressFields.city}, {addressFields.state} {addressFields.zip_code}
                    </p>
                    <p className="text-sm text-muted">{addressFields.country}</p>
                  </div>
                ) : (
                  <p className="text-sm text-muted mb-3">No address on file</p>
                )}
                <button
                  onClick={() => { setShowAddressForm(true); setAddressError(null); setAddressSuccess(false); }}
                  className="w-full py-3 rounded-xl text-sm font-semibold bg-ground border border-hairline text-ink"
                >
                  {addressFields.address_line1 ? "Edit address" : "Add address"}
                </button>
              </div>
            ) : (
              <div className="space-y-3 pt-1">
                <input
                  value={addressFields.address_line1}
                  onChange={(e) => setAddressFields(f => ({ ...f, address_line1: e.target.value }))}
                  placeholder="Address Line 1 *"
                  className="w-full py-2.5 px-3 bg-surface text-ink rounded-xl border border-hairline outline-none focus:border-accent text-sm"
                />
                <input
                  value={addressFields.address_line2}
                  onChange={(e) => setAddressFields(f => ({ ...f, address_line2: e.target.value }))}
                  placeholder="Address Line 2"
                  className="w-full py-2.5 px-3 bg-surface text-ink rounded-xl border border-hairline outline-none focus:border-accent text-sm"
                />
                <div className="flex gap-2">
                  <input
                    value={addressFields.city}
                    onChange={(e) => setAddressFields(f => ({ ...f, city: e.target.value }))}
                    placeholder="City *"
                    className="flex-1 py-2.5 px-3 bg-surface text-ink rounded-xl border border-hairline outline-none focus:border-accent text-sm"
                  />
                  <input
                    value={addressFields.state}
                    onChange={(e) => setAddressFields(f => ({ ...f, state: e.target.value }))}
                    placeholder="State *"
                    className="w-20 py-2.5 px-3 bg-surface text-ink rounded-xl border border-hairline outline-none focus:border-accent text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <input
                    value={addressFields.zip_code}
                    onChange={(e) => setAddressFields(f => ({ ...f, zip_code: e.target.value }))}
                    placeholder="ZIP Code *"
                    className="flex-1 py-2.5 px-3 bg-surface text-ink rounded-xl border border-hairline outline-none focus:border-accent text-sm"
                  />
                  <input
                    value={addressFields.country}
                    onChange={(e) => setAddressFields(f => ({ ...f, country: e.target.value }))}
                    placeholder="Country"
                    className="w-20 py-2.5 px-3 bg-surface text-ink rounded-xl border border-hairline outline-none focus:border-accent text-sm"
                  />
                </div>
                {addressError && (
                  <p className="text-xs text-error text-center">{addressError}</p>
                )}
                {addressSuccess && (
                  <p className="text-xs text-success text-center">Address saved</p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => { setShowAddressForm(false); setAddressError(null); }}
                    className="flex-1 py-3 rounded-xl text-sm font-semibold bg-ground border border-hairline text-muted"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAddress}
                    disabled={addressLoading}
                    className="flex-1 py-3 rounded-xl text-sm font-semibold bg-ink text-ground disabled:opacity-50"
                  >
                    {addressLoading ? "Saving…" : "Save address"}
                  </button>
                </div>
              </div>
            )}
          </SectionCard>
        </div>
      )}

      {/* Card transactions */}
      <div className="px-4 mt-5">
        <SectionCard title="Transactions">
          <TransactionList
            transactions={txData?.transactions || []}
            showCard={false}
          />
        </SectionCard>
      </div>
    </PageContainer>
  );
};
